import { jwtClient } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';
import type { AuthApp } from './app-registry';

export type { AuthApp };

function sessionTokenKey(app: AuthApp) {
  return `${app}_session_token`;
}

function workerUrl() {
  return (
    (import.meta.env.VITE_WORKER_URL as string) ||
    (typeof process !== 'undefined' && process.env?.VITE_WORKER_URL) ||
    'http://localhost:8787'
  );
}

/**
 * One Better Auth client per portal app (each app is mounted at its own basePath on the shared
 * backend Worker — see backend/src/auth/instance.ts). Uses the bearer plugin's token strategy,
 * not cookies: the opaque session token Better Auth returns on sign-in is persisted here and
 * replayed as `Authorization: Bearer` on every call to Better Auth's own endpoints.
 */
export function createBetterAuthClient(app: AuthApp) {
  return createAuthClient({
    baseURL: `${workerUrl()}/api/auth/${app}`,
    plugins: [jwtClient()],
    fetchOptions: {
      auth: {
        type: 'Bearer',
        token: () => localStorage.getItem(sessionTokenKey(app)) ?? '',
      },
      onSuccess: (ctx) => {
        const token = ctx.response.headers.get('set-auth-token');
        if (token) localStorage.setItem(sessionTokenKey(app), token);
      },
    },
  });
}

export type BetterAuthClient = ReturnType<typeof createBetterAuthClient>;

export function clearBetterAuthSession(app: AuthApp) {
  localStorage.removeItem(sessionTokenKey(app));
}

/**
 * Builds the backend's oauth-bridge URL (see backend/src/index.ts) for a given final
 * destination. Better Auth's OAuth callback only ever establishes a cookie session on the
 * backend's own origin — there's no built-in handoff to a bearer-token client on a different
 * origin. The bridge runs same-origin to that cookie, reads the session token off it, and
 * appends it to `returnTo` as a URL fragment.
 */
function oauthBridgeUrl(app: AuthApp, returnTo: string, inviteToken?: string | null): string {
  const url = new URL(`${workerUrl()}/api/auth/${app}/oauth-bridge`);
  url.searchParams.set('returnTo', returnTo);
  // The bridge itself ignores this; it rides here because the callbackURL is the only value
  // that survives the round trip through Google, and the backend's sign-up guard reads it off
  // the OAuth state to reject a mismatched identity before an account exists
  // (backend/src/auth/inviteSignupGuard.ts).
  if (inviteToken) url.searchParams.set('inviteToken', inviteToken);
  return url.toString();
}

export type GoogleSignInOptions = {
  /** Binds this sign-in to a workspace invite: a different Google identity is rejected server-side. */
  inviteToken?: string | null;
  /** Pre-selects an account in Google's picker (a hint only — Google lets the user override it). */
  loginHint?: string | null;
  /** Where Better Auth redirects on a rejected/failed callback, with `?error=&error_description=`. */
  errorCallbackURL?: string;
};

/**
 * Starts Google sign-in for a web app (full-page redirect through Google, then the bridge above,
 * then back to `returnTo`, e.g. `${origin}/oauth-complete?redirect=/dashboard`). Uses
 * disableRedirect so we control navigation ourselves — signIn.social's default behavior
 * navigates straight to Google without ever going through our bridge.
 */
export async function signInWithGoogle(
  authClient: BetterAuthClient,
  app: AuthApp,
  returnTo: string,
  options: GoogleSignInOptions = {}
) {
  const { data, error } = await authClient.signIn.social({
    provider: 'google',
    callbackURL: oauthBridgeUrl(app, returnTo, options.inviteToken),
    ...(options.loginHint ? { loginHint: options.loginHint } : {}),
    ...(options.errorCallbackURL ? { errorCallbackURL: options.errorCallbackURL } : {}),
    disableRedirect: true,
  });

  if (error || !data?.url) {
    throw new Error(error?.message || 'Failed to start Google sign-in');
  }

  window.location.href = data.url;
}

/**
 * Call on mount from the app's /oauth-complete page (the `returnTo` target above). Reads the
 * session token out of the URL fragment, persists it exactly like a normal sign-in would, and
 * strips it from the visible URL/history immediately.
 */
export function completeOAuthBridge(app: AuthApp): { success: boolean; error?: string } {
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const params = new URLSearchParams(hash);
  const token = params.get('session_token');
  const error = params.get('error');

  window.history.replaceState(null, '', window.location.pathname + window.location.search);

  if (token) {
    localStorage.setItem(sessionTokenKey(app), token);
    return { success: true };
  }

  return { success: false, error: error || 'No session token returned' };
}

const JWT_TTL_MS = 15 * 60 * 1000;
const JWT_REFRESH_MARGIN_MS = 60 * 1000;

/**
 * The Better Auth session token (above) authenticates calls to Better Auth's own endpoints only.
 * The resource server (workspaceProcedure, verifyAuth) checks a separate, short-lived JWT minted
 * via the jwt plugin's /token endpoint — this caches that JWT in memory (never localStorage,
 * given its 15-minute lifetime) and mirrors Firebase's getIdToken() auto-refresh semantics
 * closely enough that call sites barely change shape.
 */
export function makeGetBetterAuthToken(authClient: BetterAuthClient) {
  let cached: { token: string; fetchedAt: number } | null = null;

  async function getBetterAuthToken(): Promise<string | null> {
    if (cached && Date.now() - cached.fetchedAt < JWT_TTL_MS - JWT_REFRESH_MARGIN_MS) {
      return cached.token;
    }

    const { data, error } = await authClient.token();
    if (error || !data?.token) {
      cached = null;
      return null;
    }

    cached = { token: data.token, fetchedAt: Date.now() };
    return cached.token;
  }

  // Sign-out must call this — otherwise a stale-but-still-valid JWT for the previous account
  // keeps authenticating every request as them for up to JWT_TTL_MS after a genuinely new
  // session begins, since nothing else in this closure is reachable from outside it.
  function clearBetterAuthTokenCache(): void {
    cached = null;
  }

  return { getBetterAuthToken, clearBetterAuthTokenCache };
}

/**
 * AppShell's ProtectedRoute/WorkspaceRedirector are provider-agnostic (see shared/src/components/layout/AppShell.tsx)
 * and accept any (cb) => unsubscribe function reporting a boolean "is authenticated" state.
 * Better Auth's client doesn't expose a plain subscribe API outside a React hook, so this polls
 * getSession() — auth-gating doesn't need sub-second freshness.
 */
export function makeBetterAuthStateSubscriber(authClient: BetterAuthClient) {
  return (cb: (isAuthed: boolean) => void) => {
    let cancelled = false;

    const check = async () => {
      const { data } = await authClient.getSession();
      if (!cancelled) cb(!!data);
    };

    check();
    const interval = setInterval(check, 30_000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  };
}
